import { useCallback, useEffect, useState } from 'react';
import { Form, Modal, Spinner } from 'react-bootstrap';
import {
  getLlmPromptExamples, getLlmExampleCandidates, getLlmExampleStructure, addLlmPromptExample,
  updateLlmPromptExample, deleteLlmPromptExample,
} from '../../api/api';
import { fetchCoverImageWithCache } from '../utils/fetchCoverImageWithCache';
import './llmPrompts.css';

const fmtNum = (n) => Number(n || 0).toLocaleString('fi-FI');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fi-FI') : null);
const errMsg = (err, fallback) => err?.response?.data?.error || fallback;

const REPORT_STATE = {
  ready:    { label: 'Valmis',     cls: 'ready' },
  draft:    { label: 'Luonnos',    cls: 'draft' },
  archived: { label: 'Arkistoitu', cls: 'archived' },
};
// Same role names as the Tekijät block (TeamBlockNimikkeella)
const SYSTEM_LABELS = {
  koordinaattori: 'Koordinaattori',
  kuntotutkija: 'Kuntotutkija',
  rakennustekniikka: 'Rakennustekniikka',
  lvia: 'LVIA-järjestelmät',
  sahko: 'Sähköjärjestelmät',
};
// What an example is: the whole report or one node inside it
const KIND_LABELS = { report: 'Koko raportti', section: 'Osio', subsection: 'Alaosio', text: 'Tekstikenttä', missing: 'Puuttuu' };

function ReportStateBadge({ state }) {
  const s = REPORT_STATE[state];
  if (!s) return null;
  return <span className={`llmp-state ${s.cls}`}>{s.label}</span>;
}

// Same route as the report view: /taloyhtiokortti/:kiinteisto/raportti/:type/:reportId/:section/:sectionId
const reportUrl = ({ id_kiinteisto, type, id }, sectionType, sectionId) =>
  id_kiinteisto && type && sectionType && sectionId
    ? `/taloyhtiokortti/${id_kiinteisto}/raportti/${type}/${id}/${sectionType}/${sectionId}`
    : null;

// Opens in a new tab so an unsaved prompt draft in the dashboard isn't lost.
function ReportLink({ url, children, className, style }) {
  if (!url) return <span className={className} style={style}>{children}</span>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className={`llmp-report-link ${className || ''}`} style={style} title="Avaa raportti uuteen välilehteen">
      {children}<i className="bi bi-box-arrow-up-right ms-1" />
    </a>
  );
}

// Kiinteistö cover images ("Kansikuva"), same source and cache as the report list page.
function useCoverImages(kiinteistoIds) {
  const [covers, setCovers] = useState({});
  const key = [...new Set(kiinteistoIds.filter(Boolean))].sort().join(',');
  useEffect(() => {
    let cancelled = false;
    key.split(',').filter(Boolean).forEach((id) => {
      fetchCoverImageWithCache(id)
        .then((url) => { if (!cancelled && url) setCovers(c => (c[id] ? c : { ...c, [id]: url })); })
        .catch(() => {});
    });
    return () => { cancelled = true; };
  }, [key]);
  return covers;
}

function CoverThumb({ url, width = 96, height = 72 }) {
  return (
    <div className="llmp-thumb" style={{ width, height, backgroundImage: url ? `url(${url})` : 'none' }} aria-hidden="true">
      {!url && <i className="bi bi-building" />}
    </div>
  );
}

function KindBadge({ kind }) {
  return <span className={`llmp-kind ${kind}`}>{KIND_LABELS[kind] || kind}</span>;
}

function ExampleRow({ ex, cover, isAdmin, busy, onToggle, onView, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const kind = ex.node?.kind || (ex.section_type ? (ex.block_id ? 'subsection' : 'section') : 'report');
  return (
    <div className={`llmp-example ${ex.is_active ? '' : 'off'}`}>
      {isAdmin && (
        <div className="d-flex flex-column align-items-center flex-shrink-0" style={{ width: 64 }}
          title="Pois päältä: esimerkki pysyy listassa, mutta sitä ei käytetä generoinnissa">
          <Form.Check
            type="switch"
            id={`example-${ex.id}`}
            checked={ex.is_active}
            disabled={busy}
            onChange={(e) => onToggle(ex, e.target.checked)}
            aria-label="Käytä generoinnissa"
          />
          <span className="text-muted" style={{ fontSize: '0.66rem', lineHeight: 1.1, textAlign: 'center' }}>
            {ex.is_active ? 'Käytössä' : 'Ei käytössä'}
          </span>
        </div>
      )}
      <CoverThumb url={cover} width={80} height={60} />
      <div className="flex-grow-1 min-w-0">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <ReportLink
            url={ex.report ? reportUrl(ex.report, ex.section_type || 'yhteenveto', ex.section_id) : null}
            className="fw-semibold"
            style={{ fontSize: '0.875rem' }}
          >
            {ex.report?.name ?? `Raportti #${ex.report_id}`}
          </ReportLink>
          <ReportStateBadge state={ex.report?.state} />
          <span className="text-muted" style={{ fontSize: '0.78rem' }}>
            #{ex.report_id}
            {ex.report?.field_date && <> · kenttäpvm {fmtDate(ex.report.field_date)}</>}
          </span>
          {!ex.is_active && <span className="llmp-nav-badge">pois käytöstä</span>}
        </div>
        <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
          <KindBadge kind={kind} />
          {kind !== 'report' && (
            <span style={{ fontSize: '0.8rem' }}>{(ex.node?.path || [ex.section_type, ex.block_label].filter(Boolean)).join(' › ')}</span>
          )}
        </div>
        {ex.problem ? (
          <div className="text-danger mt-1" style={{ fontSize: '0.8rem' }}><i className="bi bi-exclamation-triangle me-1" />{ex.problem}</div>
        ) : kind !== 'report' && (
          <div className="llmp-snippet">{ex.text.slice(0, 180)}{ex.text.length > 180 ? '…' : ''}</div>
        )}
        <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
          {fmtNum(ex.chars)} merkkiä · ~{fmtNum(ex.est_tokens)} tokenia
        </div>
      </div>
      <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0">
        <button type="button" className="btn btn-sm btn-outline-secondary py-0" onClick={() => onView(ex)}>Näytä</button>
        {isAdmin && (confirmDelete ? (
          <div className="d-flex gap-1">
            <span className="text-muted" style={{ fontSize: '0.72rem' }}>Poista esimerkeistä? Raportti säilyy.</span>
            <button type="button" className="btn btn-sm btn-danger py-0" disabled={busy} onClick={() => onDelete(ex)}>Poista</button>
            <button type="button" className="btn btn-sm btn-link py-0" onClick={() => setConfirmDelete(false)}>Peru</button>
          </div>
        ) : (
          <button type="button" className="llmp-icon danger" title="Poista esimerkkilistalta (raporttia ei poisteta)" onClick={() => setConfirmDelete(true)}>
            <i className="bi bi-x-lg" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------ node tree of one report

// A selection: { sectionType, blockId, path, chars }. sectionType null = whole report,
// blockId null = the whole section.
function TreeNode({ node, sectionType, path, depth, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const nodePath = [...path, node.label];
  const isSel = !!selected && selected.sectionType === sectionType && (selected.blockId ?? null) === (node.id ?? null);
  const hasChildren = node.children?.length > 0;
  return (
    <div>
      <div className={`llmp-tree-row ${isSel ? 'selected' : ''} ${node.used ? 'used' : ''}`} style={{ paddingLeft: 8 + depth * 18 }}>
        <button
          type="button"
          className="llmp-tree-toggle"
          style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
          onClick={() => setOpen(o => !o)}
          aria-label={open ? 'Sulje' : 'Avaa'}
        >
          <i className={`bi bi-chevron-${open ? 'down' : 'right'}`} />
        </button>
        <label className="d-flex align-items-start gap-2 flex-grow-1 min-w-0 mb-0" style={{ cursor: node.used ? 'default' : 'pointer' }}>
          <input
            type="radio"
            name="example-node"
            disabled={node.used}
            checked={isSel}
            onChange={() => onSelect({ sectionType, blockId: node.id ?? null, path: nodePath, chars: node.chars })}
            className="mt-1"
          />
          <span className="min-w-0">
            <KindBadge kind={node.kind} />{' '}
            <span style={{ fontSize: '0.85rem' }}>{node.label}</span>
            <span className="text-muted" style={{ fontSize: '0.75rem' }}> · {fmtNum(node.chars)} merkkiä{node.used ? ' · jo esimerkkinä' : ''}</span>
            {!hasChildren && node.snippet && <span className="llmp-tree-snippet">{node.snippet}…</span>}
          </span>
        </label>
      </div>
      {open && hasChildren && node.children.map(child => (
        <TreeNode key={child.id} node={child} sectionType={sectionType} path={nodePath} depth={depth + 1} selected={selected} onSelect={onSelect} />
      ))}
    </div>
  );
}

function ReportTree({ reportId, promptKey, selected, onSelect }) {
  const [tree, setTree] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setTree(null);
    setError(null);
    getLlmExampleStructure(reportId, promptKey)
      .then(({ data }) => { if (!cancelled) setTree(data); })
      .catch((err) => { if (!cancelled) setError(errMsg(err, 'Raportin rakenteen lataus epäonnistui.')); });
    return () => { cancelled = true; };
  }, [reportId, promptKey]);

  if (error) return <div className="text-danger small px-2 py-1">{error}</div>;
  if (!tree) return <div className="px-2 py-1"><Spinner animation="border" size="sm" /></div>;

  const wholeSel = !!selected && selected.sectionType === null;
  return (
    <div className="llmp-tree">
      <div className={`llmp-tree-row ${wholeSel ? 'selected' : ''} ${tree.whole.used ? 'used' : ''}`} style={{ paddingLeft: 8 }}>
        <span className="llmp-tree-toggle" style={{ visibility: 'hidden' }} />
        <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: tree.whole.used ? 'default' : 'pointer' }}>
          <input
            type="radio"
            name="example-node"
            disabled={tree.whole.used}
            checked={wholeSel}
            onChange={() => onSelect({ sectionType: null, blockId: null, path: ['Koko raportti'], chars: tree.whole.chars })}
          />
          <KindBadge kind="report" />
          <span className="text-muted" style={{ fontSize: '0.75rem' }}>
            kaikki osiot jäsennettynä · {fmtNum(tree.whole.chars)} merkkiä{tree.whole.used ? ' · jo esimerkkinä' : ''}
          </span>
        </label>
      </div>
      {tree.sections.map(sec => (
        <TreeNode
          key={sec.id}
          node={{ ...sec, id: null, kind: 'section', label: `${sec.label}${sec.state === 'ready' ? ' ✓' : ''}` }}
          sectionType={sec.section_type}
          path={[]}
          depth={0}
          selected={selected}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

// ------------------------------------------------------------ add example modal

function AddExampleModal({ show, promptKey, defaultType, onClose, onAdded }) {
  const [types, setTypes] = useState([]);
  const [reportType, setReportType] = useState(defaultType || '');
  const [reportState, setReportState] = useState('ready');
  const [teamUser, setTeamUser] = useState('');
  const [teamSystem, setTeamSystem] = useState('');
  const [teamUsers, setTeamUsers] = useState([]);
  const [teamSystems, setTeamSystems] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openReport, setOpenReport] = useState(null); // report whose tree is open
  const [selected, setSelected] = useState(null);     // node selection in openReport
  const [saving, setSaving] = useState(false);
  const covers = useCoverImages((results || []).map(r => r.id_kiinteisto));

  useEffect(() => {
    if (show) {
      setReportType(defaultType || ''); setReportState('ready'); setTeamUser(''); setTeamSystem('');
      setQuery(''); setOpenReport(null); setSelected(null); setError(null);
    }
  }, [show, defaultType]);

  const search = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getLlmExampleCandidates({ key: promptKey, reportType, state: reportState, teamUser, teamSystem, q: query, limit: 30 });
      setTypes(data.report_types);
      setTeamUsers(data.team_users || []);
      setTeamSystems(data.team_systems || []);
      setResults(data.reports);
    } catch (err) {
      setError(errMsg(err, 'Raporttien haku epäonnistui.'));
    } finally {
      setLoading(false);
    }
  }, [promptKey, reportType, reportState, teamUser, teamSystem, query]);

  useEffect(() => {
    if (!show) return;
    const t = setTimeout(search, 300);
    return () => clearTimeout(t);
  }, [show, search]);

  const resetSelection = () => { setOpenReport(null); setSelected(null); };
  const toggleReport = (rep) => {
    setSelected(null);
    setOpenReport(r => (r?.id === rep.id ? null : rep));
  };

  const add = async () => {
    setSaving(true);
    setError(null);
    try {
      await addLlmPromptExample(promptKey, {
        report_id: openReport.id,
        section_type: selected.sectionType || undefined,
        block_id: selected.blockId || undefined,
      });
      onAdded();
    } catch (err) {
      setError(errMsg(err, 'Lisäys epäonnistui.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1rem' }}>Lisää esimerkki raportista</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex gap-2 mb-3 flex-wrap">
          <select className="form-select form-select-sm" style={{ width: 240 }} value={reportType} onChange={(e) => { setReportType(e.target.value); resetSelection(); }} aria-label="Raporttityyppi">
            <option value="">Kaikki raporttityypit</option>
            {types.map(t => <option key={t.type} value={t.type}>{t.name}</option>)}
          </select>
          <select className="form-select form-select-sm" style={{ width: 170 }} value={reportState} onChange={(e) => { setReportState(e.target.value); resetSelection(); }} aria-label="Raportin tila">
            <option value="ready">Valmiit raportit</option>
            <option value="draft">Luonnokset</option>
            <option value="archived">Arkistoidut</option>
            <option value="">Kaikki tilat</option>
          </select>
          <select className="form-select form-select-sm" style={{ width: 200 }} value={teamUser} onChange={(e) => { setTeamUser(e.target.value); resetSelection(); }} aria-label="Tekijä">
            <option value="">Kaikki tekijät</option>
            <option value="me">Minä (raportit joissa olen tekijänä)</option>
            {teamUsers.map(u => <option key={u.id} value={u.id}>{u.nimi}</option>)}
          </select>
          <select className="form-select form-select-sm" style={{ width: 190 }} value={teamSystem} onChange={(e) => { setTeamSystem(e.target.value); resetSelection(); }} aria-label="Rooli">
            <option value="">Kaikki roolit</option>
            {teamSystems.map(sys => <option key={sys} value={sys}>{SYSTEM_LABELS[sys] || sys}</option>)}
          </select>
          <input
            className="form-control form-control-sm flex-grow-1"
            style={{ minWidth: 200 }}
            placeholder="Hae nimellä, kohteella, raportin tai kiinteistön id:llä…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); resetSelection(); }}
          />
        </div>

        {error && <div className="alert alert-danger py-1 px-2" style={{ fontSize: '0.85rem' }}>{error}</div>}
        {loading && !results && <Spinner animation="border" size="sm" />}
        {results && results.length === 0 && <div className="text-muted small">Ei raportteja näillä hakuehdoilla.</div>}

        <div className="d-flex flex-column gap-2" style={{ maxHeight: 480, overflowY: 'auto', opacity: loading ? 0.6 : 1 }}>
          {results?.map(rep => {
            const isOpen = openReport?.id === rep.id;
            return (
              <div key={rep.id} className={`llmp-candidate-wrap ${isOpen ? 'open' : ''}`}>
                <div className="llmp-candidate" role="button" tabIndex={0} onClick={() => toggleReport(rep)} onKeyDown={(e) => { if (e.key === 'Enter') toggleReport(rep); }}>
                  <CoverThumb url={covers[rep.id_kiinteisto]} />
                  <div className="flex-grow-1 min-w-0">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span className="fw-semibold text-truncate" style={{ fontSize: '0.95rem' }}>{rep.kohteen_nimi || rep.name}</span>
                      <ReportStateBadge state={rep.state} />
                      {rep.used_count > 0 && <span className="llmp-nav-badge">{rep.used_count} esimerkkinä</span>}
                    </div>
                    <div className="text-muted text-truncate" style={{ fontSize: '0.8rem' }}>{rep.osoite || '–'}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>{rep.name} · #{rep.id} · {rep.type}</div>
                    {rep.team?.length > 0 && (
                      <div className="llmp-team" title="Tekijät (raportin tiimi)">
                        <i className="bi bi-people me-1" />
                        {rep.team.map((p, i) => (
                          <span key={p.id}>
                            {i > 0 && ', '}
                            <span className={teamUser && String(p.id) === String(teamUser) ? 'fw-semibold text-body' : ''}>{p.nimi}</span>
                            {p.systems.length > 0 && <span className="text-muted"> ({p.systems.map(sys => SYSTEM_LABELS[sys] || sys).join(', ')})</span>}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-end" style={{ minWidth: 92 }}>
                    <div className="text-muted" style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '.04em' }}>Kenttäpvm</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>{fmtDate(rep.field_date) ?? '–'}</div>
                    <div className="small text-success mt-1">{isOpen ? 'Sulje' : 'Valitse kohta'} <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`} /></div>
                  </div>
                </div>
                {isOpen && (
                  <div className="px-2 pb-2">
                    <div className="text-muted small px-2 pb-1">Valitse mitä käytetään esimerkkinä:</div>
                    <ReportTree reportId={rep.id} promptKey={promptKey} selected={selected} onSelect={setSelected} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <div className="small text-muted min-w-0 text-truncate" style={{ maxWidth: '70%' }}>
          {selected && openReport
            ? <>Valittu: <strong>#{openReport.id} {openReport.kohteen_nimi || openReport.name}</strong> › {selected.path.join(' › ')} · {fmtNum(selected.chars)} merkkiä</>
            : 'Valitse raportti ja sen kohta: koko raportti, osio, alaosio tai tekstikenttä.'}
        </div>
        <div>
          <button type="button" className="btn btn-link" onClick={onClose} disabled={saving}>Peru</button>
          <button type="button" className="btn btn-success" disabled={!selected || saving} onClick={add}>
            {saving ? 'Lisätään…' : 'Lisää esimerkiksi'}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * Examples of one prompt: live links to report nodes (whole report / section /
 * subsection / text area), parsed at generation time. Not versioned with the prompt text.
 */
export default function PromptExamples({ promptKey, isAdmin, defaultReportType }) {
  const [examples, setExamples] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [viewing, setViewing] = useState(null);
  const covers = useCoverImages((examples || []).map(e => e.report?.id_kiinteisto));

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await getLlmPromptExamples(promptKey);
      setExamples(data);
    } catch (err) {
      setError(errMsg(err, 'Esimerkkien lataus epäonnistui.'));
      setExamples([]);
    }
  }, [promptKey]);

  useEffect(() => { setExamples(null); load(); }, [load]);

  const act = async (fn) => {
    setBusy(true);
    setError(null);
    try { await fn(); await load(); }
    catch (err) { setError(errMsg(err, 'Toiminto epäonnistui.')); }
    finally { setBusy(false); }
  };

  const active = (examples || []).filter(e => e.is_active && !e.problem);
  const activeTokens = active.reduce((sum, e) => sum + e.est_tokens, 0);
  const defaultType = defaultReportType || examples?.find(e => e.report?.type)?.report.type || '';

  return (
    <div className="llmp-sheet px-3 pt-2">
      <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
        <span className="llmp-card-title">Esimerkit</span>
        {examples && (
          <span className="text-muted" style={{ fontSize: '0.78rem' }}>
            {active.length} käytössä · ~{fmtNum(activeTokens)} tokenia per generointi
          </span>
        )}
        {isAdmin && (
          <button type="button" className="llmp-add-group ms-auto" onClick={() => setAdding(true)}>
            <i className="bi bi-plus-lg me-1" />Lisää esimerkki
          </button>
        )}
      </div>
      <div className="text-muted mb-2" style={{ fontSize: '0.78rem' }}>
        Esimerkki on linkki raporttiin: koko raportti, yksi osio, alaosio tai tekstikenttä. Sisältö luetaan raportista aina generoinnin hetkellä.
        {examples && examples.length === 0 && ' Ilman esimerkkejä käytetään sovelluksen mukana tulevia vanhoja esimerkkejä.'}
      </div>

      {error && <div className="alert alert-danger py-1 px-2" style={{ fontSize: '0.85rem' }}>{error}</div>}
      {!examples && <Spinner animation="border" size="sm" className="mb-2" />}

      {examples?.map(ex => (
        <ExampleRow
          key={ex.id}
          ex={ex}
          cover={covers[ex.report?.id_kiinteisto]}
          isAdmin={isAdmin}
          busy={busy}
          onToggle={(e, v) => act(() => updateLlmPromptExample(e.id, { is_active: v }))}
          onDelete={(e) => act(() => deleteLlmPromptExample(e.id))}
          onView={setViewing}
        />
      ))}

      <AddExampleModal
        show={adding}
        promptKey={promptKey}
        defaultType={defaultType}
        onClose={() => setAdding(false)}
        onAdded={() => { setAdding(false); load(); }}
      />

      <Modal show={!!viewing} onHide={() => setViewing(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>
            {viewing?.report?.name} #{viewing?.report_id} · {(viewing?.node?.path || []).join(' › ')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="llmp-card-title mb-1">Mallille annettava esimerkki</div>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem', fontFamily: viewing?.node?.kind === 'report' ? undefined : 'inherit', maxHeight: '65vh', overflow: 'auto' }}>
            {viewing?.text}
          </pre>
        </Modal.Body>
      </Modal>
    </div>
  );
}
